import {cleanString, compareStrings, flattenJson, isCode, handleError} from "../../utils/util";
import {isEmpty} from "lodash";
import { extractFromPath } from "../../utils/pathExpander";
import { segment } from "../../consts";
import jp from 'jsonpath';

/**
 * For a given FHIR resource, create a table rendering the pre-transformed value, the transformed FHIR values, and the
 * mapping rule.
 * @param fhirPreview
 * @param mappings
 * @returns {{path: string, mapping: *, suggest: *[]|*[], value: *, version: string, key: number}[]}
 */
export function getMappingTableWithPrePostVals(fhirPreview, mappings) {
    let flattenedResource = flattenJson(fhirPreview)
    return Object.keys(flattenedResource).map(resourcePath => {
        let mappingObj = getMappingForRenderedPath(resourcePath, flattenedResource, mappings);
        let suggest = mappingObj ? getMappingErrorPrompts(resourcePath, flattenedResource[resourcePath], mappingObj) : [];
        let value = (!!mappingObj && !!mappingObj.value) ? mappingObj.value : '';
        let dynamicPath = mappingObj && mappingObj.hasDynamicPath ? mappingObj.path : null;

        return {
            path: resourcePath,
            dynamicPath,
            value: flattenedResource[resourcePath],
            mapping: value,
            version: 'latest',
            suggest: suggest,
            key: Object.keys(flattenedResource).indexOf(resourcePath)
        }
    })
}

/**
 * Get the mapping path for a rendered output path in FHIR json object
 *
 * Paths are compared using JSON object notation e.g. identifier[0].type.coding[0].display
 * @param renderedOutputPath
 * @param resource
 * @param mappings
 * @returns {*}
 */
export function getMappingForRenderedPath(renderedOutputPath, resource, mappings) {
    let endKeyOfRenderedPath = renderedOutputPath.split('.').slice(-1)

    //for static paths mapped 1-1 to a column, can do a simple string match
    //on JSON dot notation of rendered output vs mapping document value in 'path' column
    let foundStatic = mappings.filter(mapping => {
        return resource.resourceType === mapping.resourcetype &&
            mapping.path === renderedOutputPath
    });

    if (foundStatic.length > 0) {
        return foundStatic[0]
    }

    //If no static mappings are found, for dynamic paths, compare the end key of the mapping with the end key of the
    //rendered output to find the corresponding mapping
    let foundOther = [];
    let rootKeyOfRenderedPath = renderedOutputPath.split('[')[0] // will always be an array
    let dynamicPaths = mappings.filter(mapping => mapping.path.includes(segment))
    if (dynamicPaths.length > 0) {
        //eslint-disable-next-line
        foundOther = dynamicPaths.map(mappingObj => {
            let endKeyOfMapping = mappingObj.path.split('.').slice(-1)
            let rootKeyOfMapping = mappingObj.path.split('[')[0]
            if (rootKeyOfMapping === rootKeyOfRenderedPath && endKeyOfMapping[0] === endKeyOfRenderedPath[0]) {
                mappingObj.hasDynamicPath = true;
                return mappingObj
            }
        })
    } else {
        //if there are no dynamic paths, check if the mapping contains a string instead of an integer
        //in between the brackets e.g. item[TEXT_VAL].identifier
        //eslint-disable-next-line
        let arrayStringMappings = mappings.filter(mapping => {
            let matched = mapping.path.match(/\[[a-zA-Z_*#!^]+]/)
            let rootKeyOfMapping = mapping.path.split('[')[0]
            if (!!matched && rootKeyOfRenderedPath === rootKeyOfMapping) {
                return mapping
            }
        })
        if (arrayStringMappings.length > 0) {
            //eslint-disable-next-line
            foundOther = arrayStringMappings.map(mappingObj => {
                let endKeyOfMapping = mappingObj.path.split('.').slice(-1)
                if (endKeyOfMapping[0] === endKeyOfRenderedPath[0]) {
                    return mappingObj
                }
            })
        }
    }

    if (foundOther && foundOther.length > 0) {
        return foundOther.filter(found => !!found)[0]
    }
    return [];
}

/**
 * Check mappings to find any basic errors.
 * Can be expanded to include more granular checks based on aggregate
 * values in elasticsearch, single mapping exceptions, or hardcoded rules.
 * @param resourcePath
 * @param resourceValue
 * @param mappingObj
 * @returns {[]}
 */
export function getMappingErrorPrompts(resourcePath, resourceValue, mappingObj) {
    if (!mappingObj.value) {
        return []
    }
    const mappingPathIncludesResourcePath = compareStrings(mappingObj.value.toString(), resourcePath)
    const valuesMatch = cleanString(resourceValue) === cleanString(mappingObj.value)

    if (!valuesMatch && mappingObj.value.includes('_') && !mappingPathIncludesResourcePath) {
        return [{warning: true}]
    }
    return []
}

/**
 * Searches the fhirTable for row where the sampleDataKey is used with the expectedValue
 * @param {*} fhirTable 
 * @param {*} flattendFhirPreview 
 * @param {*} sampleDataKey 
 * @param {*} sampleDataValue 
 * @param {*} groovyOutputs 
 * @returns 
 */
const findValueInFhirPreview = (fhirTable, flattendFhirPreview, sampleDataKey, sampleDataValue, groovyOutputs) => {
    return fhirTable.find(({mapping, value, path, dynamicPath}) => {
        if(isCode(mapping, path)) {
            return (mapping.includes('_.' + sampleDataKey)) && (groovyOutputs[path] === flattendFhirPreview[path]);
        } else if (dynamicPath) {
            const field = extractFromPath(path.split('.')[0], 'field', true);
            const breakOutArg = dynamicPath.match(/@segment:\w+/g)[0].replace(segment, "");
            const flattendSampleDataPath = path.replace(field, breakOutArg)
            return (sampleDataKey === flattendSampleDataPath) && (value === groovyOutputs[path]);
        } else {
            return mapping.includes('_.' + sampleDataKey) && value === sampleDataValue
        }
    });
}


/**
 * Find the expected value of for a mapping. 
 * Expected value should be pulled from groovy output if the mapping contains code.
 * Otherwise expected value should come from sampleData 
 * @param {*} mapping 
 * @param {*} sampleDataKey 
 * @param {*} sampleDataValue 
 * @param {*} groovyOutputs 
 * @returns string
 */
const getExpectedMappingValue = (mapping, sampleDataKey, sampleDataValue, groovyOutputs) => {
    let expectedVal = "";
    // determine where to pull expected value from
    if(isCode(mapping.value, mapping.path)) {
        if(mapping.groovyLookupKey){
            const groovyPath = sampleDataKey.replace(sampleDataKey.split('[')[0], mapping.groovyLookupKey)
            expectedVal = groovyOutputs[groovyPath] 
        } else {
            expectedVal = groovyOutputs[mapping.path] 
        }
    } else {
        expectedVal = sampleDataValue;
    }
    return expectedVal;
}

/**
 * Searches for values that are present in sample data, but missing in fhir preview.
 * @param {object} fhirPreview
 * @param {array} mappings
 * @returns {[]}
 */

export const missingValuesSoftSearch = (fhirPreview, mappings, missingRecords) => {
  // matches indexes in array path ex: array[1].array2[3] => [1] & [3]
  const indexMatch = /\[\d*?\]/g;
  const stillMissing = [];
  missingRecords.forEach(missingRecord => {
    try {
      const isArrayPath = missingRecord.path.match(indexMatch);

      if (isArrayPath) {
        const mapping = mappings.find(m => m.path === missingRecord.path);
        const splitPath = mapping.path.split('.');

        // search for mapping with closest related path
        for (let i = 0; i < splitPath.length; i += 1) {
          const sliceIndex = i + 1;
          const pathToSearch = splitPath.slice(0, splitPath.length - sliceIndex).join('.');
          const relatedMapping = mappings.find(
            m => m.path.startsWith(pathToSearch) && m.path !== mapping.path,
          );

          if (relatedMapping) {
            const relatedMappingPath = relatedMapping.path;
            const sharedParentPath = pathToSearch.replace(indexMatch, '[*]');
            const relatedPathEnding = relatedMappingPath
              .replace(`${pathToSearch}.`, '')
              .replace(indexMatch, '[*]');
            const realPathEnding = mapping.path
              .replace(`${pathToSearch}.`, '')
              .replace(indexMatch, '[*]');

            // find all mappings with the same parent path
            const mappingsWithSharedParent = jp.query(fhirPreview, `$.${sharedParentPath}`);

            // filter parents to find matching related mapping
            const relatedMappingsQuery = `$[?(@.${relatedPathEnding}==${relatedMapping.value})]`;
            const relatedMappingParents = jp.query(mappingsWithSharedParent, relatedMappingsQuery);

            // search final mappings set for exact value
            const expectedValueQuery = `$.${realPathEnding}`;
            const expectedValueFound = relatedMappingParents.find(rmp => {
              const queryResults = jp.query(rmp, expectedValueQuery);
              // eslint-disable-next-line eqeqeq
              return queryResults.some(x => x == missingRecord.expectedVal);
            });

            if (!expectedValueFound) stillMissing.push(missingRecord);
            break;
          }
        }
      } else {
        // could not find related mappings to be sure found value is a match
        stillMissing.push(missingRecord);
      }
    } catch (e) {
      // add value to still missing if we have trouble finding it
      stillMissing.push(missingRecord);
    }
  });
  return stillMissing;
};

/**
 * Searches for values that are present in sample data, but missing in fhir preview.

 * @param {object} sampleData
 * @param {array} mappings
 * @param {object} fhirPreview
 * @param {object} groovyOutputs
 * @returns {[]}
 */
export function getMissingValues(sampleData, mappings, fhirPreview, groovyOutputs) {
    const missing = [];
    if(isEmpty(fhirPreview) || fhirPreview.loading || isEmpty(fhirPreview) || isEmpty(sampleData) || isEmpty(groovyOutputs)) return [];
    try {
        const fhirTable = getMappingTableWithPrePostVals(fhirPreview, mappings);
        const flattendFhirPreview = flattenJson(fhirPreview);
        const flattendSampleData = flattenJson(sampleData)
        // ignores "null" and boolean values
        Object.entries(flattendSampleData)
            .filter(([k, v]) => v && !['true', 'false'].includes(v.toString().toLowerCase()))
            .forEach(([sampleKey, sampleVal]) => {                     
                // see if we can find the sampleKey being used with correct value in fhirTable
                const fhirRow = findValueInFhirPreview(fhirTable, flattendFhirPreview, sampleKey, sampleVal, groovyOutputs);
                /* if we can't find the sample data key used in the fhir preview with the correct value,
                see if we can find the mapping related to the sample data key for helpful error messages */
                if (!fhirRow) {
                    let missingMapping;
                    if(sampleKey.includes('[')) {
                        //ex: address[addr@segment:addresses].period.end => address
                        const dynamicField = extractFromPath(sampleKey.split('.')[0], 'field', true);
                        //ex: address[addr@segment:addresses].period.end => .period.end
                        const pathSuffix = sampleKey.substring(sampleKey.indexOf(']') + 1);
                        if(pathSuffix) {
                            missingMapping = mappings.find(({path}) => path.includes(`${segment}${dynamicField}`) && path.includes(pathSuffix));
                            if(missingMapping){
                                const groovyLookupKey = missingMapping.path.split('[')[0];
                                missingMapping.groovyLookupKey = groovyLookupKey;
                            }
                        } else {
                            missingMapping = mappings.find(({path}) => path === sampleKey);
                        }
                    } else {
                        missingMapping = mappings.find(({value}) => {
                            const regEx = new RegExp(`\\b(_|__).${sampleKey}\\W*\\b`);
                            const matched = value.match(regEx);
                            return matched;
                        });
                    }
                    // if we have found the mapping with the mapping that corresponds with missing/incorrect info in the preview, show in error table
                    if (missingMapping) {
                        let expectedVal = getExpectedMappingValue(missingMapping, sampleKey, sampleVal, groovyOutputs);
                        const { path } = missingMapping;
                        missing.push({sampleKey, expectedVal, sampleVal, path});
                    }
                }
            })
        return missingValuesSoftSearch(fhirPreview, mappings, missing)
    } catch (error) {
        handleError(error);
        return [];
    }

}
